import { createContext, type ReactNode, useCallback, useContext, useMemo } from 'react';

import { LocalStorageKeys, useLocalStorage } from '@shared-legacy/lib/hooks';

export type UIMode = 'legacy' | 'modern';

interface UIPreferenceContextValue {
  uiMode: UIMode;
  canSwitchDesign: boolean;
  switchDesign: () => void;
}

const UIPreferenceContext = createContext<UIPreferenceContextValue | undefined>(undefined);

interface UIPreferenceProviderProps {
  children: ReactNode;
  designSwitchEnabled?: boolean;
  documentOverride?: UIMode;
  forceModernDesign?: boolean;
}

export const UIPreferenceProvider = (props: UIPreferenceProviderProps) => {
  const { children, designSwitchEnabled = false, documentOverride, forceModernDesign = false } = props;

  const [storedMode, setStoredMode] = useLocalStorage<UIMode>(LocalStorageKeys.UI_MODE_PREFERENCE, 'legacy');
  const validStoredMode = storedMode === 'modern' || storedMode === 'legacy' ? storedMode : 'legacy';
  const canSwitchDesign = !documentOverride && !forceModernDesign && designSwitchEnabled;
  // A stored choice only counts while the deployment still offers the switch.
  // Withdrawing the switch withdraws the new design with it, including from
  // people who had opted in — leaving them there with no way back is the one
  // state nobody can get out of. The stored value is kept rather than cleared,
  // so offering the switch again restores what they chose.
  const preferredMode: UIMode = designSwitchEnabled ? validStoredMode : 'legacy';
  const uiMode: UIMode = documentOverride ? documentOverride : forceModernDesign ? 'modern' : preferredMode;

  const switchDesign = useCallback(() => {
    if (!canSwitchDesign) return;
    setStoredMode(uiMode === 'modern' ? 'legacy' : 'modern');
  }, [canSwitchDesign, setStoredMode, uiMode]);

  const value = useMemo(() => ({ uiMode, canSwitchDesign, switchDesign }), [canSwitchDesign, switchDesign, uiMode]);

  return <UIPreferenceContext.Provider value={value}>{children}</UIPreferenceContext.Provider>;
};

export const useUIPreference = (): UIPreferenceContextValue => {
  const ctx = useContext(UIPreferenceContext);
  if (!ctx) throw new Error('useUIPreference must be used within UIPreferenceProvider');
  return ctx;
};
